/**
 * CRM no novo desenho, lote 3: regras puras dos filtros do Comercial (os do drawer do clone do RD), testadas em
 * `apps/web/tests/crm-l3-comercial.test.ts`. As datas viram intervalos no fuso do navegador antes de ir à API.
 */

export const STATUS = ['todos', 'andamento', 'vendido', 'perdido', 'pausado', 'nao_pausado'] as const;
export type StatusFiltro = (typeof STATUS)[number];
export const ORDENS = ['az', 'za', 'criadas_ultimo', 'criadas_primeiro', 'proxima_tarefa', 'previsao', 'contato_recente', 'contato_antigo', 'mais_qualificadas', 'menos_qualificadas', 'maior_valor', 'menor_valor', 'interacao_recente', 'interacao_antiga'] as const;
export type Ordem = (typeof ORDENS)[number];
export const VAZIOS = ['previsao', 'fonte', 'campanha', 'produtos'] as const;
export const PREDEFINIDOS = ['criadas_14', 'criadas_90', 'sem_contato_14', 'atrasadas', 'vendidas', 'perdidas', 'abertas'] as const;
export type Predefinido = (typeof PREDEFINIDOS)[number];
/** Períodos das datas: passados (hoje, últimos N dias) e futuros (próximos N dias), como no RD. */
export const PRESETS = ['hoje', '7', '14', '30', '90', 'n7', 'n30'] as const;
export type Preset = (typeof PRESETS)[number] | '';
export const ESTATISTICAS = ['andamento', 'sem_tarefa', 'atrasadas', 'sem_produtos'] as const;
export type Estatistica = (typeof ESTATISTICAS)[number];

export interface FiltrosComercial {
  responsaveis: string[];
  etapa_id: string;
  status: StatusFiltro;
  sem_tarefa: boolean;
  vazios: string[];
  nome: string;
  qualificacao: string;
  valor_min: string;
  valor_max: string;
  criada: Preset;
  contato: Preset;
  proxima: Preset;
  fechada: Preset;
  previsao: Preset;
  parceiro_id: string;
  parceiro_nome: string;
  campanha: string;
  fonte: string;
  servico_id: string;
  servico_nome: string;
  /** Review rcrm3 spec (M1, decisão 14): a vertical (empresa do grupo); casa com a negociação dela e as de "Todas". */
  vertical: string;
  predefinido: Predefinido | '';
  estatistica: { etapa_id: string; chave: Estatistica } | null;
}

export const filtrosVazios = (): FiltrosComercial => ({
  responsaveis: [],
  etapa_id: '',
  status: 'todos',
  sem_tarefa: false,
  vazios: [],
  nome: '',
  qualificacao: '',
  valor_min: '',
  valor_max: '',
  criada: '',
  contato: '',
  proxima: '',
  fechada: '',
  previsao: '',
  parceiro_id: '',
  parceiro_nome: '',
  campanha: '',
  fonte: '',
  servico_id: '',
  servico_nome: '',
  vertical: '',
  predefinido: '',
  estatistica: null,
});

/** Intervalo de um período no fuso do navegador (ISO). */
export function intervaloPreset(p: Preset, agora = new Date()): { de?: string; ate?: string } {
  if (!p) return {};
  const dia0 = new Date(agora);
  dia0.setHours(0, 0, 0, 0);
  if (p === 'hoje') return { de: dia0.toISOString(), ate: new Date(dia0.getTime() + 86_400_000 - 1).toISOString() };
  if (p.startsWith('n')) return { de: agora.toISOString(), ate: new Date(agora.getTime() + Number(p.slice(1)) * 86_400_000).toISOString() };
  return { de: new Date(agora.getTime() - Number(p) * 86_400_000).toISOString(), ate: agora.toISOString() };
}
const soData = (iso?: string) => (iso ? iso.slice(0, 10) : undefined);
const centavos = (v: string) => (v.trim() === '' || Number.isNaN(Number(v.replace(',', '.'))) ? undefined : Math.round(Number(v.replace(',', '.')) * 100));

/** Os filtros da tela como parâmetros da API (`/pipeline/crm/*`). Os predefinidos se traduzem nos filtros equivalentes. */
export function paramsDosFiltros(f: FiltrosComercial, funilId: string | undefined, agora = new Date()): Record<string, string | number | undefined> {
  const pre = f.predefinido;
  const criada = intervaloPreset(pre === 'criadas_14' ? '14' : pre === 'criadas_90' ? '90' : f.criada, agora);
  const status: StatusFiltro = pre === 'vendidas' ? 'vendido' : pre === 'perdidas' ? 'perdido' : pre === 'abertas' ? 'andamento' : f.status;
  const contato = intervaloPreset(f.contato, agora);
  const proxima = intervaloPreset(f.proxima, agora);
  const fechada = intervaloPreset(f.fechada, agora);
  const previsao = intervaloPreset(f.previsao, agora);
  return {
    funil_id: funilId,
    responsaveis: f.responsaveis.length ? f.responsaveis.join(',') : undefined,
    etapa_id: f.etapa_id || undefined,
    status,
    sem_tarefa: f.sem_tarefa ? 'true' : undefined,
    vazios: f.vazios.length ? f.vazios.join(',') : undefined,
    nome: f.nome.trim() || undefined,
    qualificacao: f.qualificacao || undefined,
    valor_min: centavos(f.valor_min),
    valor_max: centavos(f.valor_max),
    criada_de: criada.de,
    criada_ate: criada.ate,
    contato_de: contato.de,
    contato_ate: contato.ate,
    proxima_de: proxima.de,
    proxima_ate: proxima.ate,
    fechada_de: fechada.de,
    fechada_ate: fechada.ate,
    previsao_de: soData(previsao.de),
    previsao_ate: soData(previsao.ate),
    parceiro_id: f.parceiro_id || undefined,
    campanha: f.campanha.trim() || undefined,
    fonte: f.fonte.trim() || undefined,
    servico_id: f.servico_id || undefined,
    vertical: f.vertical || undefined,
    predefinido: pre === 'sem_contato_14' || pre === 'atrasadas' ? pre : undefined,
    estatistica_etapa: f.estatistica?.etapa_id,
    estatistica: f.estatistica?.chave,
  };
}

/** Quantos filtros do drawer estão ligados (o número do botão "Filtros (n)"). Responsáveis, etapa e status ficam na barra. */
export function contarFiltros(f: FiltrosComercial): number {
  const ligados = [
    f.sem_tarefa,
    f.vazios.length > 0,
    !!f.nome.trim(),
    !!f.qualificacao,
    !!f.valor_min.trim(),
    !!f.valor_max.trim(),
    !!f.criada,
    !!f.contato,
    !!f.proxima,
    !!f.fechada,
    !!f.previsao,
    !!f.parceiro_id,
    !!f.campanha.trim(),
    !!f.fonte.trim(),
    !!f.servico_id,
    !!f.vertical,
    !!f.predefinido,
  ];
  return ligados.filter(Boolean).length;
}

/** Filtros guardados numa visão salva (só os valores, sem os nomes de exibição que a tela relê). */
export const filtrosParaVisao = (f: FiltrosComercial) => ({ ...f, estatistica: null });
export function filtrosDaVisao(v: unknown): FiltrosComercial {
  const base = filtrosVazios();
  if (!v || typeof v !== 'object') return base;
  const o = v as Record<string, unknown>;
  const texto = (k: keyof FiltrosComercial) => (typeof o[k] === 'string' ? (o[k] as string) : (base[k] as string));
  const lista = (k: 'responsaveis' | 'vazios') => (Array.isArray(o[k]) ? (o[k] as unknown[]).filter((x): x is string => typeof x === 'string') : []);
  const umDe = <T extends string>(k: keyof FiltrosComercial, opcoes: readonly T[], padrao: T): T => (opcoes.includes(o[k] as T) ? (o[k] as T) : padrao);
  return {
    ...base,
    responsaveis: lista('responsaveis'),
    vazios: lista('vazios').filter((x) => (VAZIOS as readonly string[]).includes(x)),
    etapa_id: texto('etapa_id'),
    status: umDe('status', STATUS, 'todos'),
    sem_tarefa: o.sem_tarefa === true,
    nome: texto('nome'),
    qualificacao: texto('qualificacao'),
    valor_min: texto('valor_min'),
    valor_max: texto('valor_max'),
    criada: umDe('criada', ['', ...PRESETS] as const, ''),
    contato: umDe('contato', ['', ...PRESETS] as const, ''),
    proxima: umDe('proxima', ['', ...PRESETS] as const, ''),
    fechada: umDe('fechada', ['', ...PRESETS] as const, ''),
    previsao: umDe('previsao', ['', ...PRESETS] as const, ''),
    parceiro_id: texto('parceiro_id'),
    parceiro_nome: texto('parceiro_nome'),
    campanha: texto('campanha'),
    fonte: texto('fonte'),
    servico_id: texto('servico_id'),
    servico_nome: texto('servico_nome'),
    vertical: texto('vertical'),
    predefinido: umDe('predefinido', ['', ...PREDEFINIDOS] as const, ''),
  };
}

/** Confirmação pela contagem (o RD): o número digitado tem de ser o de itens selecionados, e a caixa marcada. */
export const contagemConfere = (digitado: string, n: number, ciente: boolean) => ciente && digitado.trim() !== '' && Number(digitado.trim()) === n;
